import type { TLTheme } from "@tldraw/tlschema";
import type { PlanroomEditor } from "../../editor";

// Shared style values for the built-in shapes: stroke widths, label sizes,
// dash patterns and theme colours, keyed by the record's style props.

export type SizeStyle = "s" | "m" | "l" | "xl";
export type DashStyle = "draw" | "solid" | "dashed" | "dotted";
export type FillStyle = "none" | "semi" | "solid" | "pattern" | "fill" | "lined-fill";
export type FontStyle = "draw" | "sans" | "serif" | "mono";

export const STROKE_WIDTH: Record<SizeStyle, number> = { s: 2, m: 3.5, l: 5, xl: 10 };
export const LABEL_FONT_SIZE: Record<SizeStyle, number> = { s: 18, m: 22, l: 26, xl: 32 };
export const ARROW_LABEL_FONT_SIZE: Record<SizeStyle, number> = { s: 16, m: 20, l: 24, xl: 28 };
export const HIGHLIGHT_WIDTH: Record<SizeStyle, number> = { s: 20.16, m: 26.88, l: 40.32, xl: 49.28 };
export const NOTE_SIZE = 200;
export const LABEL_PADDING = 16;

type Colors = Record<
  string,
  { solid: string; semi: string; pattern: string; fill: string; noteFill: string; noteText: string; highlightSrgb: string; highlightP3: string }
>;

export function themeColors(editor: PlanroomEditor): Colors {
  return (editor.getCurrentTheme() as unknown as { colors: { light: Colors } }).colors.light;
}
export function colorOf(editor: PlanroomEditor, name: string) {
  if(/^#[a-fA-F0-9]{6}$/.test(name))return {...themeColors(editor).black,solid:name,semi:name,pattern:name,fill:name,noteFill:name,highlightSrgb:name,highlightP3:name};
  return themeColors(editor)[name] ?? themeColors(editor).black;
}
export function fontFamily(editor: PlanroomEditor, font: string) {
  const fonts = (editor.getCurrentTheme() as TLTheme).fonts as unknown as Record<string, { fontFamily: string }>;
  return fonts[font]?.fontFamily ?? "sans-serif";
}

/** SVG stroke-dasharray for a dash style at a stroke width. */
export function dashArray(dash: DashStyle, width: number): string | undefined {
  if (dash === "dashed") return `${width * 2} ${width * 2}`;
  if (dash === "dotted") return `0 ${width * 2}`;
  return undefined;
}

/** SVG fill paint for a closed shape's fill style. */
export function fillPaint(editor: PlanroomEditor, color: string, fill: FillStyle) {
  const c = colorOf(editor, color);
  if (fill === "none") return "none";
  if (fill === "semi") return themeColors(editor).white?.solid ?? "#ffffff";
  if (fill === "solid") return c.semi;
  if (fill === "fill") return c.solid;
  return c.pattern;
}

/** A hatch pattern for "pattern" and "lined-fill" fills, by id. */
export function hatchId(color: string) {
  return `canvas-hatch-${color}`;
}

const wideGamut = typeof CSS !== "undefined" && CSS.supports?.("color", "color(display-p3 1 1 1)");
/** Highlighter colour, in wide gamut where the browser supports it. */
export function highlightColor(editor: PlanroomEditor, color: string) {
  const c = colorOf(editor, color);
  return wideGamut ? c.highlightP3 : c.highlightSrgb;
}
